import Phaser from 'phaser';
import { BLOCK_JUMP_START_X, COIN_JUMP_START_X, GAME_HEIGHT, GROUND_Y, GRAVITY_Y, JUMP_SPEED, PLAYER_SPEED, QUESTION_BLOCK, WORLD_WIDTH } from '../config/game';
import { Player } from '../objects/Player';
import { QuestionBlock } from '../objects/QuestionBlock';
import { Star } from '../objects/Star';
import { isExactHeadHit, jumpPositionY } from '../utils/motion';
import { addGround, addMuteButton, addSky } from './sceneArt';
import { AudioManager } from '../systems/AudioManager';
import { birthdayConfig } from '../config/birthday';

export class LevelScene extends Phaser.Scene {
  private player!: Player;
  private block!: QuestionBlock;
  private audio!: AudioManager;
  private coinJumped = false;
  private blockJumped = false;
  private jumpStartedAt?: number;
  private jumpGroundY = GROUND_Y;
  private activeJump?: 'coins' | 'block';
  private pausedAtBlock = false;
  private levelStarted = false;
  private koopa?: Phaser.GameObjects.Sprite;
  private coins: Phaser.GameObjects.Sprite[] = [];
  private transitionStarted = false;
  private debugText?: Phaser.GameObjects.Text;
  private previousHeadY = 0;
  private reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

  constructor() { super('LevelScene'); }
  create(): void {
    this.cameras.main.fadeIn(260, 23, 33, 66);
    this.audio = new AudioManager(this); this.audio.unlock();
    addSky(this, true);
    addGround(this);
    this.add.image(480, GROUND_Y, 'pipe-bottom').setDisplaySize(64, 64).setOrigin(0.5, 1).setDepth(4);
    this.add.image(480, GROUND_Y - 64, 'pipe-top').setDisplaySize(72, 32).setOrigin(0.5, 1).setDepth(5);
    [770, 818, 866, 914].forEach((x) => this.add.image(x, 570, 'brick').setDisplaySize(48, 48).setDepth(4));
    // Keep the castle visible at the right edge when the question block is hit.
    this.add.image(1280, GROUND_Y, 'castle').setDisplaySize(120, 120).setOrigin(0.5, 1).setDepth(3);
    this.block = new QuestionBlock(this, QUESTION_BLOCK.x, QUESTION_BLOCK.y);
    [[540, 570], [575, 535], [610, 572]].forEach(([x, y], index) => {
      // Decorative coins must not be affected by world gravity.
      const coin = this.add.sprite(x, y, 'coin').setDisplaySize(32, 32).setDepth(5);
      this.tweens.add({ targets: coin, y: coin.y - 7, duration: 350, yoyo: true, repeat: -1, delay: index * 60 });
      this.coins.push(coin);
    });
    this.koopa = this.add.sprite(850, GROUND_Y, 'koopa-walk-1').setDisplaySize(48, 48).setOrigin(0.5, 1).setDepth(7).play('koopa-walk');
    this.player = new Player(this, 50, GROUND_Y);
    this.player.run(0);
    this.previousHeadY = this.player.y - this.player.displayHeight;
    this.cameras.main.setBounds(0, 0, WORLD_WIDTH, GAME_HEIGHT);
    this.cameras.main.startFollow(this.player, true, this.reducedMotion ? 1 : 0.09, this.reducedMotion ? 1 : 0.09, -65, 75);
    addMuteButton(this, () => this.audio.toggle());
    this.debugText = this.add.text(8, 44, '', { fontFamily: 'monospace', fontSize: '10px', color: '#ffffff', backgroundColor: '#172142' }).setScrollFactor(0).setDepth(100).setVisible(false);
    this.input.keyboard?.on('keydown-D', () => this.debugText?.setVisible(!this.debugText?.visible));
    this.audio.play('hurry-up');
    this.time.delayedCall(this.reducedMotion ? 200 : 1000, () => { this.levelStarted = true; this.player.run(0); });
  }

  update(_: number, delta: number): void {
    if (this.transitionStarted) return;
    if (!this.levelStarted) return;
    if (this.pausedAtBlock) {
      return;
    }
    if (this.jumpStartedAt === undefined) {
      // This is a fully scripted runner. Pinning the sprite on every grounded
      // frame keeps its feet at one exact visual floor height.
      this.player.setY(GROUND_Y);
      this.player.land();
    }
    // This is a scripted sequence: advance horizontally by elapsed time instead
    // of relying on collision resolution for horizontal velocity.
    this.player.x += PLAYER_SPEED * (delta / 1000);
    if (this.koopa) this.koopa.x -= 75 * (delta / 1000);
    this.collectCoins();
    if (!this.coinJumped && this.player.x >= COIN_JUMP_START_X) this.startJump('coins');
    // Start late enough that the jump apex meets the centre of the 48px block.
    if (!this.blockJumped && this.activeJump === undefined && this.player.x >= BLOCK_JUMP_START_X) this.startJump('block');
    if (this.jumpStartedAt !== undefined) this.updateScriptedJump();
    const playerTop = this.player.y - this.player.displayHeight;
    this.debugText?.setText(`DEBUG  top: ${playerTop.toFixed(1)} | box bottom: ${this.block.underside.toFixed(1)} | Δ: ${(playerTop - this.block.underside).toFixed(1)}`);
    this.previousHeadY = this.player.y - this.player.displayHeight;
  }

  private onExactBlockHit(): void {
    this.pausedAtBlock = true;
    this.block.activate();
    this.audio.play('block-hit');
    const star = new Star(this, this.block.x, this.block.y - 26);
    const particles = this.add.particles(star.x, star.y, 'spark', { speed: { min: 28, max: 94 }, lifespan: 430, quantity: 2, scale: { start: 0.8, end: 0 }, emitting: false, blendMode: 'ADD' }).setDepth(11);
    star.emerge(() => {
      this.audio.play('star');
      particles.explode(18);
      this.time.delayedCall(this.reducedMotion ? 180 : 520, () => this.unlock(star));
    });
  }

  private updateScriptedJump(): void {
    const elapsed = (this.time.now - this.jumpStartedAt!) / 1000;
    const flightTime = (-2 * JUMP_SPEED) / GRAVITY_Y;
    if (elapsed >= flightTime) {
      // Return to the exact Y from which this particular jump started.
      this.player.setY(this.jumpGroundY);
      this.jumpStartedAt = undefined;
      this.activeJump = undefined;
      this.player.land();
      return;
    }
    this.player.setY(jumpPositionY(this.jumpGroundY, elapsed, JUMP_SPEED, GRAVITY_Y));
    const velocityY = JUMP_SPEED + GRAVITY_Y * elapsed;
    const currentHeadY = this.player.y - this.player.displayHeight;
    const blockBottom = this.block.y + this.block.displayHeight / 2;
    if (this.activeJump === 'block' && !this.block.used && isExactHeadHit({ previousTop: this.previousHeadY, currentTop: currentHeadY, blockBottom, velocityY })) this.onExactBlockHit();
  }

  private startJump(kind: 'coins' | 'block'): void {
    if (kind === 'coins') this.coinJumped = true;
    else this.blockJumped = true;
    this.activeJump = kind;
    this.jumpGroundY = this.player.y;
    this.jumpStartedAt = this.time.now;
    this.player.jump(0);
    this.audio.play('jump');
  }

  private collectCoins(): void {
    for (const coin of this.coins) {
      if (!coin.active || Phaser.Math.Distance.Between(this.player.x, this.player.y, coin.x, coin.y) > 34) continue;
      coin.disableInteractive().setActive(false);
      this.tweens.add({ targets: coin, y: coin.y - 22, alpha: 0, scale: 1.5, duration: 160, onComplete: () => coin.destroy() });
      this.audio.play('coin');
    }
  }

  private unlock(star: Star): void {
    this.transitionStarted = true;
    this.audio.play('unlocked');
    this.player.play('hero-run-1');
    const overlay = this.add.rectangle(this.cameras.main.midPoint.x, GAME_HEIGHT / 2, 420, GAME_HEIGHT, 0x10162e, 0).setScrollFactor(0).setDepth(30);
    const unlocked = this.add.text(195, 285, `LEVEL ${birthdayConfig.age}\nFREIGESCHALTET!`, { fontFamily: 'PixelFallback, monospace', fontSize: '25px', align: 'center', color: '#ffd32a', stroke: '#172142', strokeThickness: 7 }).setOrigin(0.5).setScrollFactor(0).setDepth(32).setAlpha(0);
    this.tweens.add({ targets: overlay, alpha: 0.7, duration: this.reducedMotion ? 160 : 520 });
    this.tweens.add({ targets: star, x: this.cameras.main.midPoint.x, y: 360, scale: 1.5, duration: this.reducedMotion ? 250 : 900, ease: 'Quad.inOut' });
    this.tweens.add({ targets: unlocked, alpha: 1, duration: this.reducedMotion ? 180 : 460, delay: this.reducedMotion ? 120 : 360, onComplete: () => this.time.delayedCall(this.reducedMotion ? 300 : 1150, () => this.scene.start('InvitationScene')) });
  }
}
